import { useEffect, useMemo } from 'react';

import { Path } from 'constants/routing';
import { FileType } from 'services/file.service';
import { ConfigFlags } from 'services/clip.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { getPath } from 'components/_hooks/use-router';
import { selectClipData } from 'components/app/_helpers/_app.helper';
import { getSbOrderClips } from 'components/app/_helpers/app-config.helper';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { EffectsAdjustment, setEffectsAdjustment } from 'components/app/editor/_mediator/editor.slice';
import { speedIsEnabled } from 'components/app/editor/_mediator/editor-clips/editor-clips-linking.helper';
import { useSbPanZoomSeek } from 'components/app/storyboard/sb-pan-zoom/use-sb-pan-zoom-seek';
import { SPEED_SLOW, SPEED_FAST, CONFIGS_SLIDERS_CLIP } from './_config-panel.params';
import { useClipConfig } from './use-clip-config';

import { Tooltip } from 'components/UI/tooltip/tooltip';
import { InputCheck } from 'components/UI/input/elements/input-check';
import { TooltipHelp } from 'components/UI/tooltip/tooltip-help';
import { ConfigSlider } from './elements/config-slider';
import { ConfigPanZoom } from './elements/config-pan-zoom';
import { ConfigPanZoomAR } from './elements/config-pan-zoom-ar';
import { ConfigRotate } from './elements/config-rotate';
import styles from './_editor-config.module.scss';

const CONFIGS = [
  { key: ConfigFlags.reverse, label: ConfigFlags[ConfigFlags.reverse].toString(), forImage: false },
  { key: ConfigFlags.flip, label: ConfigFlags[ConfigFlags.flip].toString(), forImage: true },
  { key: ConfigFlags.stabilization, label: ConfigFlags[ConfigFlags.stabilization].toString(), forImage: false },
];

type Checks = Record<keyof typeof ConfigFlags, boolean>;

const CHECKS: Checks = Object.keys(ConfigFlags)
  .filter((key) => isNaN(Number(key)))
  .reduce((acc, key) => {
    acc[key as keyof typeof ConfigFlags] = false;
    return acc;
  }, {} as Checks);

export const ConfigPanelClip = () => {
  const linkedClips = useAppSelector((state) => state.editor.linkedClips);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const activeClip = useAppSelector((state) => state.editor.activeClip);
  const effectsAdjustment = useAppSelector((state) => state.editor.effectsAdjustment);
  const files = useAppSelector((state) => state.app__files.files);
  const helpMode = useAppSelector(selectHelpMode);
  const sbSeekToKey = useSbPanZoomSeek();
  const dispatch = useAppDispatch();
  const path = getPath();

  const getFlagValue = (flag: ConfigFlags): boolean => {
    const key = ConfigFlags[flag] as keyof typeof ConfigFlags;
    return flags[key];
  };

  const clipData = useMemo(() => {
    if (!clipsMap || !activeClip) return;
    return selectClipData(activeClip, clipsMap);
  }, [clipsMap, activeClip]);

  const flags = useMemo(() => {
    if (!clipData?.config.flags) return CHECKS;
    const res = { ...CHECKS };
    for (const key of Object.keys(res) as (keyof typeof ConfigFlags)[]) {
      const enumValue = ConfigFlags[key];
      res[key] = clipData.config.flags.includes(enumValue);
    }
    return res;
  }, [clipData?.config.flags]);

  const isSpeedDisabled = useMemo(() => {
    if (!clipData) return true;
    return !speedIsEnabled(clipData, linkedClips);
  }, [clipData]);

  const { handleConfigs, handleOrder, handleSpeed, handleEffects, handleRotate } = useClipConfig(clipData);
  const { firstClipOrder, lastClipOrder } = useMemo(() => getSbOrderClips(clipsMap), [clipsMap]);
  const isPanZoom = getFlagValue(ConfigFlags.panZoomRotate);

  useEffect(() => {
    if (!clipData) return;
    dispatch(
      setEffectsAdjustment({
        brightness: clipData.config.brightness,
        contrast: clipData.config.contrast,
        volume: clipData.config.volume,
      })
    );
  }, [clipData]);

  if (!clipData) return null;

  const isImage = files[clipData.mediaFileId]?.fileType === FileType.image;

  return (
    <div className={styles.Config}>
      <h1 className={styles.Config__title}>{path === Path.storyboard ? 'Clip' : 'Highlight'} Config menu</h1>
      <ul>
        {(isImage ? CONFIGS.filter(({ forImage }) => forImage) : CONFIGS).map(({ key, label }) => (
          <InputCheck key={key} value={getFlagValue(key)} setValue={() => handleConfigs(key)} title={label} />
        ))}
        <ConfigRotate rotate={clipData.config.rotate} onClick={() => handleRotate(clipData.config.rotate)} />

        <li className={styles.Config__subTitle}>Order</li>
        <InputCheck
          title={`First${firstClipOrder && clipData.clipId !== firstClipOrder ? ` (${firstClipOrder})` : ''}`}
          value={getFlagValue(ConfigFlags.orderFirst)}
          setValue={() => handleOrder(ConfigFlags.orderFirst)}
          disabled={!!firstClipOrder && clipData.clipId !== firstClipOrder}
        />
        <InputCheck
          title={`Last${lastClipOrder && clipData.clipId !== lastClipOrder ? ` (${lastClipOrder})` : ''}`}
          value={getFlagValue(ConfigFlags.orderLast)}
          setValue={() => handleOrder(ConfigFlags.orderLast)}
          disabled={!!lastClipOrder && clipData.clipId !== lastClipOrder}
        />
        {!isImage && (
          <Tooltip
            showTooltip={helpMode && isSpeedDisabled}
            position="left"
            tip={
              <TooltipHelp className={styles.Config__tooltip}>
                <p>
                  Speed is DISABLED:
                  <br />
                  Both <u>Start tag</u> and <u>End tag</u> are defined
                </p>
              </TooltipHelp>
            }
          >
            <div>
              <li className={styles.Config__subTitle}>Speed</li>
              <InputCheck
                title="0.5x"
                value={clipData.config.speed === SPEED_SLOW}
                setValue={() => handleSpeed(SPEED_SLOW)}
                disabled={isSpeedDisabled}
              />
              <InputCheck
                title="2x"
                value={clipData.config.speed === SPEED_FAST}
                setValue={() => handleSpeed(SPEED_FAST)}
                disabled={isSpeedDisabled}
              />
            </div>
          </Tooltip>
        )}

        <div className={styles.Config__divider} />

        {(isImage ? CONFIGS_SLIDERS_CLIP.filter(({ forImage }) => forImage) : CONFIGS_SLIDERS_CLIP).map(
          ({ key, label, params }) => (
            <div key={key}>
              <InputCheck value={getFlagValue(key)} setValue={() => handleConfigs(key)} title={label} />
              {getFlagValue(key) && (
                <ConfigSlider
                  params={params}
                  value={effectsAdjustment[label as keyof EffectsAdjustment]}
                  setValue={(value) => dispatch(setEffectsAdjustment({ [label]: value }))}
                  onSubmit={handleEffects}
                />
              )}
            </div>
          )
        )}

        <div className={styles.Config__ar}>
          <InputCheck
            title="Pan & Zoom"
            value={isPanZoom}
            setValue={() => {
              handleConfigs(ConfigFlags.panZoomRotate);
              if (!isPanZoom) sbSeekToKey(0);
            }}
          />
          {isPanZoom && <ConfigPanZoomAR />}
        </div>
        {isPanZoom && <ConfigPanZoom />}
      </ul>
    </div>
  );
};
